import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../supabase";
import { useAuth } from "../contexts/AuthContext";
import { useProfileInfo } from "../contexts/ProfileInfoContext";
import { useProfileViews } from "../hooks/useProfileViews";
import { sendCandidateConfirmation, sendEmployerNotification } from "../services/emailService";
import { checkRateLimit } from "../utils/rateLimiter";

const EmployeeCompanyDetail = () => {
    const { id: employerId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { profileInfo } = useProfileInfo();
    const { trackProfileView } = useProfileViews();
    const [companyData, setCompanyData] = useState(null);
    const [jobListings, setJobListings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [hiredCount, setHiredCount] = useState(0);
    const [companyLogoUrl, setCompanyLogoUrl] = useState(null);

    // Helper function to capitalize first letter of words
    const capitalizeFirstLetter = (str) => {
        if (!str) return str;
        // Handle hyphenated words like "full-time" -> "Full-time"
        return str.split('-').map(word => 
            word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        ).join('-');
    };

    // Match category pill classes used in company search, so colors stay consistent
    const getCategoryClass = (category) => {
        if (!category) return "";
        const categoryMap = {
            "Business/Consulting": "business-consulting",
            "Clinical Medicine": "clinical-medicine",
            "Clinical Research": "clinical-research",
            "Entrepreneurship": "entrepreneurship",
            "HealthTech": "health-tech",
            "Hospital/Clinic Management": "hospital-clinic-management",
            "Insurance": "insurance",
            "Marketing/Sales": "marketing-sales",
            "Medical Communication": "medical-communication",
            "Medical Education": "medical-education",
            "Pharmaceuticals": "pharmaceuticals",
            "Policy/Legal/Ethics": "policy-legal-ethics",
            "Project Management": "project-management",
            "Wellness/lifestyle": "wellness-lifestyle"
        };
        return categoryMap[category] || "business-consulting";
    };

    // Get social media icon based on platform
    const getSocialIcon = (platform, url) => {
        switch(platform.toLowerCase()) {
            case 'linkedin':
                return (
                    <a href={url} target="_blank" rel="noopener noreferrer" className="w-6 h-6 text-black hover:text-blue-700 transition-colors">
                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                        </svg>
                    </a>
                );
            case 'facebook':
                return (
                    <a href={url} target="_blank" rel="noopener noreferrer" className="w-6 h-6 text-black hover:text-blue-600 transition-colors">
                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                        </svg>
                    </a>
                );
            case 'twitter':
                return (
                    <a href={url} target="_blank" rel="noopener noreferrer" className="w-6 h-6 text-black hover:text-gray-800 transition-colors">
                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                        </svg>
                    </a>
                );
            case 'instagram':
                return (
                    <a href={url} target="_blank" rel="noopener noreferrer" className="w-6 h-6 text-black hover:text-pink-600 transition-colors">
                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.155c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                        </svg>
                    </a>
                );
            case 'youtube':
                return (
                    <a href={url} target="_blank" rel="noopener noreferrer" className="w-6 h-6 text-black hover:text-red-600 transition-colors">
                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                        </svg>
                    </a>
                );
            case 'website':
                return (
                    <a href={url} target="_blank" rel="noopener noreferrer" className="w-6 h-6 text-black hover:text-gray-800 transition-colors">
                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                        </svg>
                    </a>
                );
            case 'other':
                return (
                    <a href={url} target="_blank" rel="noopener noreferrer" className="w-6 h-6 text-black hover:text-purple-600 transition-colors">
                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/>
                        </svg>
                    </a>
                );
            default:
                return null;
        }
    };

    // Format posting date - show when the job listing was posted/started
    const formatPostingDate = (job) => {
        if (!job) return 'Date not available';
        
        let postingDate;
        
        // Use created_at (when job was posted)
        if (job.created_at) {
            postingDate = new Date(job.created_at);
        } 
        // Fallback: use current date if created_at is not available
        else {
            postingDate = new Date();
        }
        
        // Format as DD/MM/YYYY
        const day = String(postingDate.getDate()).padStart(2, '0');
        const month = String(postingDate.getMonth() + 1).padStart(2, '0');
        const year = postingDate.getFullYear();
        
        return `${day}/${month}/${year}`;
    };

    useEffect(() => {
        if (employerId) {
            fetchCompanyData();
        }
    }, [employerId]);

    const fetchCompanyData = async () => {
        try {
            setLoading(true);
            
            // Fetch job listings for this employer
            const { data: jobs, error: jobsError } = await supabase
                .from('internal_job_listings')
                .select('*')
                .eq('employer_id', employerId)
                .eq('status', 'published')
                .order('created_at', { ascending: false });

            if (jobsError) throw jobsError;

            if (jobs && jobs.length > 0) {
                // Use the first job to get company info (all jobs from same employer will have same company data)
                const firstJob = jobs[0];
                // Prefer province/state + country if available, fallback to legacy location string
                // Only use location field if province and country are both empty
                const companyLocation = (firstJob.province || firstJob.country)
                    ? [firstJob.province, firstJob.country].filter(Boolean).join(', ')
                    : (firstJob.location || '');
                const newCompanyData = {
                    company_name: firstJob.company_name,
                    location: companyLocation,
                    industry: firstJob.industry,
                    company_description: firstJob.company_description,
                    company_url: firstJob.company_url,
                    contact_email: firstJob.contact_email,
                    logo_filename: firstJob.logo_filename,
                    logo_url: firstJob.logo_url || null,
                    social_media_links: firstJob.social_media_links || []
                };

                setCompanyData(newCompanyData);

                // Set company logo URL from job listing if already available
                if (newCompanyData.logo_url) {
                    setCompanyLogoUrl(newCompanyData.logo_url);
                } else if (newCompanyData.logo_filename) {
                    // Fallback: fetch from storage using the same pattern as JobDetail/EmployeeCompanySearch
                    fetchCompanyLogo(newCompanyData.logo_filename);
                }
                setJobListings(jobs);
                
                // Fetch hired count for this employer's job listings
                const jobIds = jobs.map(job => job.id);
                if (jobIds.length > 0) {
                    const { count, error: countError } = await supabase
                        .from('applications')
                        .select('*', { count: 'exact', head: true })
                        .in('job_listing_id', jobIds);
                    
                    if (!countError && count !== null) {
                        setHiredCount(count - 1);
                    }
                }
                
                // Track company profile view
                if (user && user.id !== employerId) {
                    const context = {
                        source: 'employee_company_search',
                        profileRole: 'Employer',
                        referrer: document.referrer,
                        userAgent: navigator.userAgent
                    };
                    
                    trackProfileView(employerId, context);
                }
            } else {
                setError("No job listings found for this company");
            }
        } catch (err) {
            setError("Failed to load company data");
        } finally {
            setLoading(false);
        }
    };

    const fetchCompanyLogo = async (logoFilename) => {
        try {
            const bucket = process.env.REACT_APP_PROFILE_IMAGES_BUCKET || "profile-images";

            const { data: files, error: listError } = await supabase.storage
                .from(bucket)
                .list(employerId, {
                    limit: 100,
                    offset: 0,
                    sortBy: { column: "created_at", order: "desc" }
                });

            if (!listError && files && files.length > 0) {
                const logoFiles = files.filter((f) => f.name.startsWith("company_logo_"));

                if (logoFiles.length > 0) {
                    let logoFile = null;

                    if (logoFilename) {
                        logoFile = logoFiles.find(
                            (f) =>
                                f.name.endsWith(`_${logoFilename}`) ||
                                f.name.endsWith(logoFilename) ||
                                f.name.includes(logoFilename)
                        );
                    }

                    if (!logoFile) {
                        logoFile = logoFiles.sort((a, b) => {
                            const aTime = a.created_at || a.updated_at || "";
                            const bTime = b.created_at || b.updated_at || "";
                            return bTime.localeCompare(aTime);
                        })[0];
                    }

                    if (logoFile) {
                        const logoPath = `${employerId}/${logoFile.name}`;
                        const { data: logoUrlData } = supabase.storage.from(bucket).getPublicUrl(logoPath);
                        if (logoUrlData?.publicUrl) {
                            setCompanyLogoUrl(logoUrlData.publicUrl);
                        }
                    }
                }
            }
            } catch (err) {
                // Error fetching company logo
            }
    };

    const handleApply = async (jobId) => {
        if (!user) {
            alert("Please log in to apply for jobs");
            return;
        }

        // Rate limit: Max 20 applications per hour per user
        const rateLimitKey = `application_${user.id}`;
        if (!checkRateLimit(rateLimitKey, 20, 3600000)) {
            alert("Rate limit exceeded. You can submit up to 20 applications per hour. Please try again later.");
            return;
        }

        try {
            const now = new Date().toISOString();
            
            // Get job details for email
            const { data: jobData } = await supabase
                .from('internal_job_listings')
                .select('*')
                .eq('id', jobId)
                .single();

            const { data: applicationData, error } = await supabase
                .from('applications')
                .upsert({
                    user_id: user.id,
                    job_listing_id: jobId,
                    status: 'applied',
                    stage: 'Applications',
                    application_date: now,
                    last_updated: now
                }, { onConflict: 'user_id,job_listing_id' })
                .select()
                .single();

            if (error) throw error;
            
            // Send email to candidate
            const candidateName = profileInfo?.firstname && profileInfo?.lastname
                ? `${profileInfo.firstname} ${profileInfo.lastname}`
                : profileInfo?.firstname || profileInfo?.lastname || user?.email?.split('@')[0] || 'Candidate';
            
            sendCandidateConfirmation(
                user.email,
                candidateName,
                jobData?.title || 'the position',
                jobData?.company_name || companyData?.company_name || 'the company',
                now
            );

            // Send email to employer (if we have a contact email)
            const employerEmail = jobData?.contact_email || companyData?.contact_email;
            if (employerEmail) {
                const employerName = jobData?.company_name || companyData?.company_name || "Hiring Manager";
                const applicationLink = `https://doctorsjobs.io/employer/job-detail/${jobId}`;

                sendEmployerNotification(
                    employerEmail,
                    employerName,
                    candidateName,
                    user.email,
                    jobData?.title || "the position",
                    now,
                    applicationLink
                );
            }
            
            alert("Application submitted successfully!");
        } catch (err) {
            alert("Failed to submit application. Please try again.");
        }
    };

    if (loading) {
        return (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="text-center">Loading company data...</div>
            </div>
        );
    }

    if (error || !companyData) {
        return (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="text-center text-red-600">{error || "Company not found"}</div>
            </div>
        );
    }

    const hasSocialLinks = Array.isArray(companyData?.social_media_links) && companyData.social_media_links.length > 0;

    return (
        <>
        <style>{`
            @media (max-width: 1024px) {
                .employee-company-detail-container {
                    padding: 1rem !important;
                    padding-top: 1rem !important;
                    padding-bottom: 1rem !important;
                }
                .employee-company-detail-container h1 {
                    font-size: 1.75rem !important;
                    margin-bottom: 1rem !important;
                }
                .employee-company-detail-about {
                    padding: 1rem !important;
                }
                .employee-company-detail-about h2 {
                    font-size: 1.25rem !important;
                    margin-bottom: 1rem !important;
                }
                .employee-company-detail-sidebar {
                    margin-top: 1rem !important;
                }
                .employee-company-detail-sidebar-card {
                    padding: 1rem !important;
                }
                .employee-company-detail-sidebar-card h3 {
                    font-size: 1rem !important;
                    margin-bottom: 0.75rem !important;
                }
                .employee-company-detail-jobs-section {
                    padding: 1rem !important;
                }
                .employee-company-detail-jobs-section h2 {
                    font-size: 1.25rem !important;
                    margin-bottom: 1rem !important;
                }
                .employee-company-detail-table {
                    overflow-x: auto !important;
                    -webkit-overflow-scrolling: touch !important;
                }
                .employee-company-detail-table-header {
                    display: none !important;
                }
                .employee-company-detail-table-row {
                    display: flex !important;
                    flex-direction: column !important;
                    padding: 1rem !important;
                    gap: 0.75rem !important;
                    border-bottom: 1px solid #e5e7eb !important;
                    background: white !important;
                    border-radius: 0.5rem !important;
                    margin-bottom: 0.75rem !important;
                }
                .employee-company-detail-table-row > div {
                    display: none !important;
                }
                .employee-company-detail-table-row .employee-company-detail-mobile-card {
                    display: flex !important;
                    flex-direction: column !important;
                    gap: 0.5rem !important;
                }
                .employee-company-detail-mobile-card-title {
                    font-weight: 600 !important;
                    font-size: 0.875rem !important;
                    color: #111827 !important;
                    margin-bottom: 0.25rem !important;
                }
                .employee-company-detail-mobile-card-level {
                    font-size: 0.75rem !important;
                    color: #6b7280 !important;
                    margin-bottom: 0.5rem !important;
                }
                .employee-company-detail-mobile-card-category {
                    margin-bottom: 0.5rem !important;
                }
                .employee-company-detail-mobile-card-category span {
                    font-size: 0.75rem !important;
                    padding: 0.375rem 0.75rem !important;
                }
                .employee-company-detail-mobile-card-type {
                    font-size: 0.75rem !important;
                    color: #374151 !important;
                    margin-bottom: 0.5rem !important;
                }
                .employee-company-detail-mobile-card-location {
                    font-size: 0.75rem !important;
                    color: #6b7280 !important;
                    margin-bottom: 0.75rem !important;
                }
                .employee-company-detail-mobile-card-button {
                    width: 100% !important;
                    margin-top: 0.5rem !important;
                    padding: 0.625rem 1rem !important;
                    font-size: 0.875rem !important;
                }
                .employee-company-detail-mobile-card-deadline {
                    font-size: 0.75rem !important;
                    color: #6b7280 !important;
                    margin-top: 0.5rem !important;
                }
            }
        `}</style>
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 employee-company-detail-container">
                {/* Company Header */}
                <div className="mb-8">
                    <div className="flex items-center gap-4">
                        {companyLogoUrl ? (
                            <img
                                src={companyLogoUrl}
                                alt={`${companyData.company_name} logo`}
                                className="w-14 h-14 rounded-full object-cover bg-gray-100 flex-shrink-0"
                            />
                        ) : (
                            <div className="w-14 h-14 rounded-full bg-gray-100 flex-shrink-0" />
                        )}
                        <h1 className="text-4xl font-bold text-black">{companyData.company_name}</h1>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 items-start">
                    {/* Main Content */}
                    <div className="lg:col-span-3 space-y-8 w-full">
                        {/* About Section */}
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 employee-company-detail-about">
                            <h2 className="text-2xl font-bold text-black mb-6">About {companyData.company_name}</h2>
                            
                            {/* Location */}
                            <div className="flex items-center gap-2 mb-4">
                                <svg className="w-4 h-4 text-gray-500" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                                </svg>
                                <span className="text-gray-600">{companyData.location}</span>
                            </div>
                            
                            <div className="text-gray-700 leading-relaxed space-y-4">
                                <p>
                                    {companyData.company_description || "As an employer, we provide ample opportunities for people who have a passion for serving others, to make a real difference in the quality of life of our citizens."}
                                </p>
                                {companyData.core_values ? (
                                    <p>
                                        Our core set of values are: <strong>{companyData.core_values}</strong>
                                    </p>
                                ) : (
                                    <p className="opacity-0 h-6">
                                        {/* Placeholder to maintain spacing */}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Sidebar */}
                    <div className="lg:col-span-1 employee-company-detail-sidebar">
                        <div className="space-y-6 h-fit">
                            {/* Connect with us */}
                            <div className="bg-white border border-gray-200 rounded-2xl p-6 employee-company-detail-sidebar-card">
                                <h3 className="text-lg font-bold text-black mb-4">Connect with us</h3>
                                <div className="flex flex-col gap-3">
                                    {hasSocialLinks && (
                                        <div className="flex gap-3 flex-wrap">
                                            {companyData.social_media_links.map((link, index) => (
                                                <div key={index}>
                                                    {getSocialIcon(link.platform, link.url)}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {companyData.company_url && (
                                        <a 
                                            href={companyData.company_url.startsWith('http') ? companyData.company_url : `https://${companyData.company_url}`}
                                            className={`block text-sm text-blue-600 hover:text-blue-800 underline break-words ${hasSocialLinks ? "pt-2 border-t border-gray-100" : ""}`}
                                            style={{ wordBreak: 'break-word' }}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {companyData.company_url}
                                        </a>
                                    )}
                                </div>
                            </div>

                            {/* Industry and Hired Cards - Side by Side */}
                            <div className="grid grid-cols-5 gap-4">
                                {/* Industry Card (slightly wider) */}
                                <div className="bg-white border border-gray-200 rounded-2xl p-4 employee-company-detail-sidebar-card col-span-3">
                                    <div className="text-sm text-gray-500 mb-2">Industry</div>
                                    <div className="flex justify-center mt-2">
                                        <span className={`inline-flex rounded-full text-xs font-medium border category-pill category-pill--sm ${getCategoryClass(companyData.industry)}`}>
                                            {companyData.industry || 'Clinical Medicine'}
                                        </span>
                                    </div>
                                </div>

                                {/* Hired Card */}
                                <div className="bg-white border border-gray-200 rounded-2xl p-4 employee-company-detail-sidebar-card col-span-2">
                                    <div className="text-sm text-gray-500 mb-2">Hired</div>
                                    <div className="text-3xl font-bold text-black">{hiredCount}</div>
                                    <div className="text-sm text-gray-500">positions</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Open Jobs Section - Full Width */}
            <div className="w-full px-4 sm:px-6 lg:px-8 pb-8">
                <div className="max-w-7xl mx-auto">
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 employee-company-detail-jobs-section">
                        <div className="mb-6">
                            <h2 className="text-2xl font-bold text-black">{jobListings.length} Open Jobs</h2>
                        </div>
                        
                        {/* Jobs Table */}
                        <div className="w-full border border-gray-200 rounded-xl overflow-hidden employee-company-detail-table">
                            {/* Table Header */}
                            <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200 employee-company-detail-table-header">
                                <div className="grid grid-cols-[28px,1fr,1fr,1fr,1fr,1fr] gap-2 text-sm font-semibold text-black">
                                    <div className="flex items-center">
                                        <input type="checkbox" className="rounded border-gray-300 text-black focus:ring-black accent-black" />
                                    </div>
                                    <div>Job Title</div>
                                    <div>Job Sector</div>
                                    <div>Position Type</div>
                                    <div>Location</div>
                                    <div className="pl-2">Application</div>
                                </div>
                            </div>

                            {/* Job Rows */}
                            <div className="divide-y divide-gray-100">
                                {jobListings.map((job, index) => (
                                    <div key={job.id} className="w-full px-6 py-5 hover:bg-gray-50 transition-colors employee-company-detail-table-row">
                                        {/* Desktop Grid View */}
                                        <div className="w-full grid grid-cols-[28px,1fr,1fr,1fr,1fr,1fr] gap-2 items-center hidden sm:grid">
                                            <div className="flex items-center">
                                                <input 
                                                    type="checkbox" 
                                                    defaultChecked={index < 2}
                                                    className="rounded border-gray-300 text-black focus:ring-black accent-black" 
                                                />
                                            </div>
                                            <div>
                                                <div className="font-semibold text-black text-sm">{job.title}*</div>
                                                <div className="text-xs text-gray-500 mt-1">Senior Level</div>
                                            </div>
                                            <div>
                                                <span className={`inline-flex rounded-full text-xs font-medium border category-pill category-pill--sm ${getCategoryClass(job.specialty)}`}>
                                                    {job.specialty || 'Clinical Medicine'}
                                                </span>
                                            </div>
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-gray-700 text-sm">{capitalizeFirstLetter(job.employment_type)}</span>
                                                </div>
                                            </div>
                                            <div>
                                                <div className="flex items-center gap-1">
                                                    <svg className="w-3 h-3 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                                                        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                                                    </svg>
                                                    <span className="text-gray-700 text-sm">{[job.province, job.country].filter(Boolean).join(', ') || job.location}</span>
                                                </div>
                                            </div>
                                            <div className="pl-4">
                                                <button 
                                                    onClick={() => navigate(`/job/${job.id}`)}
                                                    className="bg-black text-white rounded-full px-3 py-1.5 text-sm hover:bg-gray-800 whitespace-nowrap"
                                                >
                                                    View job post
                                                </button>
                                                <div className="text-xs text-gray-500 mt-2 flex items-center gap-1 whitespace-nowrap">
                                                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                                                        <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
                                                    </svg>
                                                    <span>Posted: {formatPostingDate(job)}</span>
                                                </div>
                                            </div>
                                        </div>
                                        {/* Mobile Card View */}
                                        <div className="sm:hidden employee-company-detail-mobile-card">
                                            <div className="employee-company-detail-mobile-card-title">{job.title || 'N/A'}</div>
                                            <div className="employee-company-detail-mobile-card-level">Senior Level</div>
                                            <div className="employee-company-detail-mobile-card-category">
                                                <span className={`inline-flex rounded-full text-xs font-medium border category-pill category-pill--sm ${getCategoryClass(job.specialty)}`}>
                                                    {job.specialty || 'Clinical Medicine'}
                                                </span>
                                            </div>
                                            <div className="employee-company-detail-mobile-card-type">{capitalizeFirstLetter(job.employment_type || 'N/A')}</div>
                                            <div className="employee-company-detail-mobile-card-location">
                                                <svg className="w-3 h-3 text-gray-400 inline-block mr-1" fill="currentColor" viewBox="0 0 24 24">
                                                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                                                </svg>
                                                {[job.province, job.country].filter(Boolean).join(', ') || job.location || 'N/A'}
                                            </div>
                                            <button 
                                                onClick={() => navigate(`/job/${job.id}`)}
                                                className="bg-black text-white rounded-full px-3 py-1.5 text-sm hover:bg-gray-800 employee-company-detail-mobile-card-button"
                                            >
                                                View job post
                                            </button>
                                            <div className="employee-company-detail-mobile-card-deadline">
                                                <svg className="w-3 h-3 inline-block mr-1" fill="currentColor" viewBox="0 0 24 24">
                                                    <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
                                                </svg>
                                                Posted: {formatPostingDate(job)}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        </>
    );
};

export default EmployeeCompanyDetail;